import type { IUserMe } from "@root/api/services/users/me";
import { useGlobalContext } from "@root/context";
import { type ComponentProps, type ComponentType, type ReactNode, type Ref, createElement, forwardRef } from "react";
import { Navigate, type To } from "react-router-dom";

export const isAuthorize = (
  role: IUserMe["roleV2"],
  requiredPermissions: string[],
  isSuper = false,
  condition: "or" | "and" = "and"
) => {
  if (!role) {
    return false;
  }
  if (isSuper && role.isSuper) {
    return true;
  }
  for (const permission of requiredPermissions) {
    if (condition === "and" && role.permissions.findIndex((userPermission) => userPermission === permission) === -1) {
      return false;
    }
    if (condition === "or" && role.permissions.findIndex((userPermission) => userPermission === permission) !== -1) {
      return true;
    }
  }
  return condition === "and";
};

export const useAuthorizer = (
  requiredPermissions: string[],
  isSuper = false,
  condition: "or" | "and" = "and",
  userRole?: IUserMe["roleV2"]
) => {
  const globalContext = useGlobalContext();
  const role = userRole ?? globalContext?.user?.roleV2;
  return isAuthorize(role, requiredPermissions, isSuper, condition);
};
// biome-ignore lint/suspicious/noExplicitAny: replacement of eslint ignore
interface IAuthorizerProps<F extends ComponentType<any>, A extends ComponentType<any>> {
  userRole?: IUserMe["roleV2"];
  requiredPermissions: string[];
  condition?: "or" | "and";
  redirect?: To;
  Forbidden?: F;
  ForbiddenProps?: ComponentProps<F>;
  Authorized?: A;
  AuthorizedProps?: ComponentProps<A>;
  isSuper?: boolean;
  children?: ReactNode | ReactNode[];
}

// biome-ignore lint/suspicious/noExplicitAny: replacement of eslint ignore
export const Authorizer = forwardRef(function Authorizer<F extends ComponentType<any>, A extends ComponentType<any>>(
  {
    userRole,
    requiredPermissions,
    condition = "and",
    redirect,
    // biome-ignore lint/style/useNamingConvention: would need a refacto of all authorizer
    Forbidden,
    // biome-ignore lint/style/useNamingConvention: would need a refacto of all authorizer
    ForbiddenProps,
    // biome-ignore lint/style/useNamingConvention: would need a refacto of all authorizer
    Authorized,
    // biome-ignore lint/style/useNamingConvention: would need a refacto of all authorizer
    AuthorizedProps,
    isSuper,
    children,
  }: IAuthorizerProps<F, A>,
  ref: Ref<F | A>
) {
  const isAuthorized = useAuthorizer(requiredPermissions, isSuper, condition, userRole);
  if (isAuthorized) {
    return Authorized ? createElement(Authorized, { ...AuthorizedProps, ref }) : <>{children}</>;
  }
  if (redirect) {
    return <Navigate to={redirect} replace />;
  }
  if (Forbidden) {
    return createElement(Forbidden, { ...ForbiddenProps, ref });
  }
  return null;
});
